import React, { useEffect, useState } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { getShopCookie, SHOP_META, DEFAULT_SHOP } from "../lib/shop-context";

/**
 * Phase 32 hotfix 2 — bridge for legacy un-prefixed order URLs
 * (`/pedido/{id}`) so links from old emails, WhatsApp messages, and
 * `CheckoutSuccessPage` continue to render the OrderConfirmationPage inside
 * the correct shop AppShell (`/{shop}/pedido/{id}`).
 *
 * Resolution order:
 *   1. Fetch `GET /orders/{id}` and use `order.shop` if the caller is
 *      authorised (owns the order, or matches its guest session).
 *   2. Fall back to the `mercago_shop` cookie so a signed-out visitor
 *      following a link still lands in their last-visited shop.
 *   3. Fall back to the default shop (envio) as a last resort.
 *
 * The `?…` search string is always preserved across the redirect.
 */
export default function LegacyOrderRedirect() {
  const { id } = useParams();
  const location = useLocation();
  const [target, setTarget] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      let shop = null;
      try {
        const { data } = await api.get(`/orders/${id}`);
        if (data?.shop === "envio" || data?.shop === "local") {
          shop = data.shop;
        }
      } catch (_e) {
        // 401/403/404 — fall through to cookie/default.
      }
      if (!shop) shop = getShopCookie() || DEFAULT_SHOP;
      const prefix = (SHOP_META[shop] || SHOP_META[DEFAULT_SHOP]).prefix;
      if (alive) setTarget(`${prefix}/pedido/${id}${location.search || ""}`);
    })();
    return () => {
      alive = false;
    };
  }, [id, location.search]);

  if (!target) {
    // Keep the visitor on a neutral placeholder while we resolve the shop
    // so they never see a flash of the 404 page.
    return (
      <div
        data-testid="legacy-order-redirect-loading"
        className="mx-auto max-w-2xl px-6 py-24 text-center"
      >
        <div className="h-32 rounded-2xl bg-neutral-100 animate-pulse" />
        <p className="mt-4 text-sm text-neutral-500">Cargando tu pedido…</p>
      </div>
    );
  }
  return <Navigate to={target} replace />;
}
